import { formatISO, getTime } from 'date-fns';
import { decryptData, encryptData } from '@/lib/encryptionUtils';
import { supabase } from '@/lib/supabase';
import { type Collection, type Section } from '@/features/collections/types/collection.types';
import type { Page, PageMetadata } from '@/features/pages/types/page.types';
import { getCurrentUser } from '../auth/AuthService';
import { getSectionProperties } from '@/features/collections/repositories/CollectionRepository';
import { orderBy } from 'lodash-es';
import type { SupabaseCollection, SupabaseNote } from '@/types/supabase';

export type Note = SupabaseNote;

export type SbCollection = SupabaseCollection;

interface WithId {
  id: string;
}

export class RemoteDBService {
  private supabase = supabase;
  private readonly maxRetries = 2;
  private readonly retryDelay = 1000;
  // Per-row requests (updates have no bulk API) run this many at a time so a
  // large restore doesn't exhaust the browser connection pool or rate limits.
  private readonly maxConcurrentRequests = 10;

  private async runWithConcurrency<T>(items: T[], task: (item: T) => Promise<void>): Promise<void> {
    for (let i = 0; i < items.length; i += this.maxConcurrentRequests) {
      await Promise.all(items.slice(i, i + this.maxConcurrentRequests).map(task));
    }
  }

  private get userId(): string | null {
    return getCurrentUser()?.uid ?? null;
  }

  private ensureAuthenticated(): string {
    const userId = this.userId;

    if (!userId) {
      throw new Error('User must be authenticated to perform this operation');
    }

    return userId;
  }

  /**
   * One-time reassignment of rows created under the legacy Firebase UID to the
   * current Supabase user. No-op unless an unclaimed firebase_user_map entry
   * matches the signed-in user's email. Safe to call on every sync.
   */
  async claimLegacyData(): Promise<void> {
    this.ensureAuthenticated();
    const { error } = await this.supabase.rpc('claim_firebase_data');
    if (error) throw error;
  }

  private async executeBatchWithRetry(
    operation: () => Promise<void>,
    retryCount = 0
  ): Promise<void> {
    try {
      await operation();
    } catch (error) {
      console.error(`Operation failed (attempt ${String(retryCount + 1)}):`, error);
      if (retryCount < this.maxRetries) {
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));
        return this.executeBatchWithRetry(operation, retryCount + 1);
      }
      throw error;
    }
  }

  private async processBatchOperations<T>(
    notes: T[],
    operation: (notes: T[]) => Promise<void>
  ): Promise<void> {
    const BATCH_SIZE = 1000; // Supabase allows larger batches than Firestore

    const chunks = Array.from({ length: Math.ceil(notes.length / BATCH_SIZE) }, (_, i) =>
      notes.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
    );

    for (const chunk of chunks) {
      await this.executeBatchWithRetry(async () => {
        await operation(chunk);
      });
    }
  }

  async bulkAddNotes(items: Page[]): Promise<void> {
    const userId = this.ensureAuthenticated();
    if (!items.length) return;

    await this.processBatchOperations(items, async chunk => {
      const notes = await Promise.all(
        chunk.map(async item => ({
          ...(await this.convertToNote(item)),
          id: item.id,
          userId: userId,
        }))
      );

      const { error } = await this.supabase.from('Note').upsert(notes, {
        onConflict: 'id',
        ignoreDuplicates: false,
      });

      if (error) throw error;
    });
  }

  async bulkUpdateNotes(pages: Page[]): Promise<void> {
    const userId = this.ensureAuthenticated();
    if (!pages.length) return;

    await this.processBatchOperations(pages, async chunk => {
      // Supabase doesn't support bulk updates in a single query
      // We need to handle each update individually
      await this.runWithConcurrency(chunk, async page => {
        const { error } = await this.supabase
          .from('Note')
          .update({
            ...(await this.convertToNote(page)),
          })
          .eq('id', page.id)
          .eq('userId', userId);

        if (error) throw error;
      });
    });
  }

  async bulkDeleteItems(table: 'Collection' | 'Note', items: WithId[]): Promise<void> {
    const userId = this.ensureAuthenticated();
    if (!items.length) return;

    await this.processBatchOperations(items, async chunk => {
      const ids = chunk.map(item => item.id);
      const { error } = await this.supabase.from(table).delete().in('id', ids).eq('userId', userId);

      if (error) throw error;
    });
  }

  async bulkAddCollections(collections: Collection[]): Promise<void> {
    this.ensureAuthenticated();
    if (!collections.length) return;

    await this.processBatchOperations(collections, async chunk => {
      // Supabase doesn't support bulk updates in a single query
      // We need to handle each update individually
      await this.runWithConcurrency(chunk, async coll => {
        const { error } = await this.supabase
          .from('Collection')
          .upsert(this.convertToSupabaseCollection(coll), {
            onConflict: 'id',
            ignoreDuplicates: false,
          });

        if (error) throw error;
      });
    });
  }

  async bulkUpdateCollections(collections: Collection[]): Promise<void> {
    const userId = this.ensureAuthenticated();
    if (!collections.length) return;

    await this.processBatchOperations(collections, async chunk => {
      // Supabase doesn't support bulk updates in a single query
      // We need to handle each update individually
      await this.runWithConcurrency(chunk, async coll => {
        const { error } = await this.supabase
          .from('Collection')
          .update({
            ...this.convertToSupabaseCollection(coll),
          })
          .eq('id', coll.id)
          .eq('userId', userId);

        if (error) throw error;
      });
    });
  }

  async getCollectionChangesSince(lastSyncTime: number | null): Promise<Collection[]> {
    const sbCollections = await this.getTableChangesSince<SbCollection>(
      'Collection',
      lastSyncTime,
      100
    );
    return sbCollections.map(c => this.convertToLocalCollection(c));
  }

  async getCollectionChangesBatch(
    lastSyncTime: number | null,
    startIndex: number,
    pageSize: number
  ): Promise<{ batch: Collection[]; hasMore: boolean; total: number | null }> {
    try {
      const userId = this.ensureAuthenticated();

      // First, get the count of total records to sync if this is the first batch
      let total: number | null = null;
      if (startIndex === 0) {
        const countQuery = this.supabase
          .from('Collection')
          .select('id', { count: 'exact', head: true })
          .eq('userId', userId);

        if (lastSyncTime) {
          countQuery.gt('updatedAt', new Date(lastSyncTime).toISOString());
        }

        const { count, error: countError } = await countQuery;
        if (countError) throw countError;
        total = count;
      }

      // Get the actual batch of collections
      let query = this.supabase
        .from('Collection')
        .select('*')
        .eq('userId', userId)
        .range(startIndex, startIndex + pageSize - 1)
        .order('updatedAt'); // Oldest first so that if error, we can pick up using normal sync

      if (lastSyncTime) {
        query = query.gt('updatedAt', new Date(lastSyncTime).toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const hasMore = data.length === pageSize;
      const batchCollections = data as SbCollection[];
      const mappedCollections = batchCollections.map(c => this.convertToLocalCollection(c));

      return {
        batch: mappedCollections,
        hasMore,
        total,
      };
    } catch (error) {
      console.error('Error fetching collection batch:', error);
      throw error;
    }
  }

  async getPageChangesSince(lastSyncTime: number | null): Promise<Page[]> {
    const notes = await this.getTableChangesSince<Note>('Note', lastSyncTime);
    return Promise.all(notes.map(n => this.convertToPage(n)));
  }

  async getPageChangesBatch(
    lastSyncTime: number | null,
    startIndex: number,
    pageSize: number
  ): Promise<{ batch: Page[]; hasMore: boolean; total: number | null }> {
    try {
      const userId = this.ensureAuthenticated();

      // First, get the count of total records to sync if this is the first batch
      let total: number | null = null;
      if (startIndex === 0) {
        const countQuery = this.supabase
          .from('Note')
          .select('id', { count: 'exact', head: true })
          .eq('userId', userId);

        if (lastSyncTime) {
          countQuery.gt('updatedAt', new Date(lastSyncTime).toISOString());
        }

        const { count, error: countError } = await countQuery;
        if (countError) throw countError;
        total = count;
      }

      // Get the actual batch of notes
      let query = this.supabase
        .from('Note')
        .select('*')
        .eq('userId', userId)
        .range(startIndex, startIndex + pageSize - 1)
        .order('updatedAt'); // Oldest first so that if error, we can pick up using normal sync

      if (lastSyncTime) {
        query = query.gt('updatedAt', new Date(lastSyncTime).toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const hasMore = data.length === pageSize;
      const batchNotes = data as Note[];
      const batchPages = await Promise.all(batchNotes.map(n => this.convertToPage(n)));

      return {
        batch: batchPages,
        hasMore,
        total,
      };
    } catch (error) {
      console.error('Error fetching page batch:', error);
      throw error;
    }
  }

  // Note pages stay small because rows include full note content, which can
  // be several MB for notes with embedded images. Collection rows carry no
  // note content, so they can page much larger.
  private async getTableChangesSince<T>(
    table: 'Note' | 'Collection',
    lastSyncTime: number | null,
    pageSize = 10
  ): Promise<T[]> {
    const userId = this.ensureAuthenticated();
    let allPages: T[] = [];
    let hasMore = true;
    let start = 0;

    try {
      while (hasMore) {
        let query = this.supabase
          .from(table)
          .select('*')
          .eq('userId', userId)
          .range(start, start + pageSize - 1)
          .order('updatedAt', { ascending: true });

        if (lastSyncTime) {
          query = query.gt('updatedAt', new Date(lastSyncTime).toISOString());
        }

        const { data, error } = await query;

        if (error) throw error;
        if (data.length === 0) {
          hasMore = false;
          break;
        }

        allPages = allPages.concat(data as T[]);

        if (data.length < pageSize) {
          hasMore = false;
        }

        start += pageSize;
      }

      return allPages;
    } catch (error) {
      console.error('Error fetching changes:', error);
      throw error;
    }
  }

  async getPublicNote(id: string, collectionId?: string): Promise<Page | null> {
    try {
      const query = await this.supabase
        .from('Note')
        .select('*')
        .eq('id', id)
        .is('archivedAt', null)
        .single();

      if (query.error) return null;

      // Visibility is enforced by RLS: anonymous clients only receive notes
      // that are individually public or belong to a public collection.
      const note = query.data;
      if (collectionId && note.collectionId !== collectionId) return null;

      return await this.convertToPage(note);
    } catch (error) {
      console.error('Error fetching changes:', error);
      throw error;
    }
  }

  async getPublicCollections(subdomain: string): Promise<Collection[]> {
    try {
      // Use the Supabase RPC function to get collections by subdomain
      const query = await this.supabase.rpc('get_public_collections_by_subdomain', {
        subdomain_param: subdomain,
      });

      if (query.error) throw query.error;

      const collections = query.data;
      return collections.map((c: SbCollection) => this.convertToLocalCollection(c));
    } catch (error) {
      console.error('Error fetching public collections:', error);
      throw error;
    }
  }

  async getPublicCollectionById(id: string) {
    const collection = await this.getPublicCollectionRecord(id);
    if (!collection) throw new Error('Public collection not found');

    return this.convertToLocalCollection(collection);
  }

  async getCollectionByIdAndFirstPage(id: string) {
    const dbCollection = await this.getPublicCollectionRecord(id);
    if (!dbCollection) throw new Error('Public collection not found');

    const collection = this.convertToLocalCollection(dbCollection);
    const orderedSections = orderBy(collection.sections as Section[], 'orderNumber');
    const firstSection = orderedSections.at(0);
    const firstPage = firstSection ? await this.getPageBySectionId(firstSection.id) : null;
    return {
      collection,
      firstPage,
    };
  }

  async getPageBySectionId(sectionId: string) {
    const s = await supabase
      .from('Note')
      .select('*')
      .eq('sectionId', sectionId)
      .eq('orderNumber', 0)
      .is('archivedAt', null)
      .maybeSingle();

    if (s.error) return null;

    const note: Note | null = s.data ?? null;
    if (!note) return null;

    return this.convertToPage(note);
  }

  async getCollectionNotesMetadata(collectionId: string): Promise<PageMetadata[]> {
    try {
      const query = await this.supabase
        .from('Note')
        .select(
          'id, title, createdAt, updatedAt, archivedAt, isPublic, tags, sectionId, collectionId, orderNumber'
        )
        .is('archivedAt', null)
        .eq('collectionId', collectionId);

      const { data, error } = query;

      if (error) throw error;

      const notes = data as Omit<Note, 'content' | 'userId'>[];
      return await Promise.all(notes.map(n => this.convertToPageMetadata(n)));
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      throw error;
    }
  }

  async getCollectionNotes(collectionId: string): Promise<Page[]> {
    try {
      const query = await this.supabase
        .from('Note')
        .select('*')
        .is('archivedAt', null)
        .eq('collectionId', collectionId);

      const { data, error } = query;

      if (error) throw error;

      const notes = data as Note[];
      return await Promise.all(notes.map(n => this.convertToPage(n)));
    } catch (error) {
      console.error('Error fetching changes:', error);
      throw error;
    }
  }

  // Convert from Supabase Note to your Page type
  private async convertToPage(note: Note): Promise<Page> {
    return {
      id: note.id,
      title: await decryptData(note.title),
      content: await decryptData(note.content),
      createdAt: getTruncatedTime(note.createdAt),
      updatedAt: note.updatedAt ? getTruncatedTime(note.updatedAt) : Date.now(),
      archivedAt: note.archivedAt ? getTruncatedTime(note.archivedAt) : 0,
      isPublic: note.isPublic || false,
      tags: this.parseJSONBTags(note),
      sectionId: note.sectionId,
      collectionId: note.collectionId,
      orderNumber: note.orderNumber || 0,
    };
  }

  private async getPublicCollectionRecord(id: string): Promise<SbCollection | null> {
    const response = await this.supabase
      .from('Collection')
      .select('*')
      .eq('id', id)
      .eq('isPublic', true)
      .is('archivedAt', null)
      .maybeSingle();

    if (response.error) return null;

    const collection: SbCollection | null = response.data ?? null;
    return collection;
  }

  // Convert from Supabase Note to PageMetadata (without content)
  private async convertToPageMetadata(
    note: Omit<Note, 'content' | 'userId'>
  ): Promise<PageMetadata> {
    return {
      id: note.id,
      title: await decryptData(note.title),
      createdAt: getTruncatedTime(note.createdAt),
      updatedAt: note.updatedAt ? getTruncatedTime(note.updatedAt) : Date.now(),
      archivedAt: note.archivedAt ? getTruncatedTime(note.archivedAt) : 0,
      isPublic: note.isPublic || false,
      tags: this.parseJSONBTags(note as Note),
      sectionId: note.sectionId,
      collectionId: note.collectionId,
      orderNumber: note.orderNumber || 0,
    };
  }

  // Convert from your Page type to Supabase Note
  private async convertToNote(page: Page): Promise<Omit<Note, 'id' | 'user_id'>> {
    const normalizedTags = normalizeTags(page.tags || []);
    return {
      userId: this.userId || '',
      createdAt: formatISO(page.createdAt),
      updatedAt: formatISO(page.updatedAt),
      tags: JSON.stringify(normalizedTags),
      archivedAt: page.archivedAt ? formatISO(page.archivedAt) : null,
      title: await encryptData(page.title),
      content: await encryptData(page.content),
      isPublic: page.isPublic || false,
      sectionId: page.sectionId || '',
      collectionId: page.collectionId || '',
      orderNumber: page.orderNumber || 0,
    };
  }

  // Convert from your Page type to Supabase Collection
  private convertToSupabaseCollection(coll: Collection): SbCollection {
    const normalizedTags = normalizeTags(coll.tags || []);
    return {
      id: coll.id,
      userId: this.userId || '',
      title: coll.title,
      description: coll.description || '',
      isPublic: !!coll.isPublic,
      isFavourite: !!coll.isFavourite,
      createdAt: formatISO(coll.createdAt),
      updatedAt: formatISO(coll.updatedAt),
      archivedAt: coll.archivedAt ? formatISO(coll.archivedAt) : null,
      tags: JSON.stringify(normalizedTags),
      sections: JSON.stringify((coll.sections || []).map(getSectionProperties)),
    };
  }

  // Convert from Supabase Note to your Page type
  private convertToLocalCollection(coll: SbCollection): Collection {
    return {
      id: coll.id,
      title: coll.title,
      description: coll.description,
      isPublic: coll.isPublic,
      isFavourite: coll.isFavourite,
      createdAt: getTruncatedTime(coll.createdAt),
      updatedAt: coll.updatedAt ? getTruncatedTime(coll.updatedAt) : Date.now(),
      archivedAt: coll.archivedAt ? getTruncatedTime(coll.archivedAt) : 0,
      tags: this.parseJSONBTags(coll),
      sections: coll.sections ? this.parseJSONBSections(coll).map(getSectionProperties) : [],
    };
  }

  private parseJSONBTags(item: Note | SbCollection): string[] {
    let parsedTags: string[] = [];
    try {
      const tagsData =
        typeof item.tags === 'string'
          ? (JSON.parse(item.tags) as string[])
          : (item.tags as string[]);
      parsedTags = Array.isArray(tagsData) ? tagsData : [];
    } catch (e) {
      console.error('Error parsing tags:', e);
      parsedTags = [];
    }
    return parsedTags;
  }

  private parseJSONBSections(item: SbCollection): Section[] {
    let parsedSections: Section[] = [];
    try {
      const sectionsData =
        typeof item.sections === 'string'
          ? (JSON.parse(item.sections) as Section[])
          : (item.sections as unknown as Section[]);
      parsedSections = Array.isArray(sectionsData) ? sectionsData : [];
    } catch (e) {
      console.error('Error parsing sections:', e);
      parsedSections = [];
    }
    return parsedSections;
  }
}

const normalizeTags = (tags: string[]): string[] => {
  if (!Array.isArray(tags)) return [];

  return Array.from(
    new Set(
      tags
        .map(tag => tag.trim().toLowerCase()) // Normalize
        .filter(
          (tag): tag is string => typeof tag === 'string' && tag.length > 0 && tag.length <= 100 // Type guard to ensure non-null
        )
    )
  ).slice(0, 50); // Limit to 50 tags
};

const getTruncatedTime = (isoDate: string) => ((getTime(new Date(isoDate)) / 1000) | 0) * 1000;

const remoteDbService = new RemoteDBService();
export default remoteDbService;
